import { SocialNetworkList } from '../helpers/sellerApplicationFormValidator';
import { SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';

import { SellerApplicationForm_viewer$data as SalesforceLeadResponse } from './__generated__/SellerApplicationForm_viewer.graphql';

type ApplicationSteps =
    | 'BUSINESS_INTRO'
    | 'BUSINESS_STEP1'
    | 'BUSINESS_STEP2'
    | 'BUSINESS_STEP3'
    | 'BUSINESS_STEP4'
    | 'BUSINESS_STEP5'
    | 'CONSIGNORS_STEP'
    | 'CONSIGNORS_STEP_ITEMS'
    | 'CONSIGNORS_STEP_ITEM_UPLOAD'
    | 'SELECTION_STEP'
    | 'BUSINESS_CONFIRMATION'
    | 'CONSIGNORS_CONFIRMATION';

export const FURNITURE_VINTAGE = 'Furniture - Vintage and Antique';
export const FURNITURE_CONTEMPORARY = 'Furniture - 21st Century (New and Custom)';
export const FINE_ART = 'Fine Art';
export const JEWELRY_ESTATE = 'Jewelry - Estate';
export const JEWELRY_CONTEMPORARY = 'Jewelry - 21st Century (Contemporary)';
export const FASHION_VINTAGE = 'Fashion - Vintage';
export const FASHION_CONTEMPORARY = 'Fashion - Contemporary';

const SF_FURNITURE_VINTAGE = 'Furniture';
const SF_FURNITURE_CONTEMPORARY = '21st Century Furniture';
const SF_FINE_ART = 'Fine Art';
const SF_JEWELRY_ESTATE = 'Jewelry';
const SF_JEWELRY_CONTEMPORARY = '21st Century Jewelry';
const SF_FASHION_VINTAGE = 'Vintage Fashion';
const SF_FASHION_CONTEMPORARY = 'Contemporary Fashion';

const SF_PRIMARY_BUSINESS_FOCUS = [
    SF_FURNITURE_VINTAGE,
    SF_FURNITURE_CONTEMPORARY,
    SF_FINE_ART,
    SF_JEWELRY_ESTATE,
    SF_JEWELRY_CONTEMPORARY,
    SF_FASHION_VINTAGE,
    SF_FASHION_CONTEMPORARY,
] as const;

export const primaryBusinessFocusMapToSF = {
    [FURNITURE_VINTAGE]: SF_FURNITURE_VINTAGE,
    [FURNITURE_CONTEMPORARY]: SF_FURNITURE_CONTEMPORARY,
    [FINE_ART]: SF_FINE_ART,
    [JEWELRY_ESTATE]: SF_JEWELRY_ESTATE,
    [JEWELRY_CONTEMPORARY]: SF_JEWELRY_CONTEMPORARY,
    [FASHION_VINTAGE]: SF_FASHION_VINTAGE,
    [FASHION_CONTEMPORARY]: SF_FASHION_CONTEMPORARY,
} as const;

type SFPrimaryBusinessFocus = (typeof SF_PRIMARY_BUSINESS_FOCUS)[number];

export type PrimaryBusinessFocus = keyof typeof primaryBusinessFocusMapToSF;

type SFPrimaryBusinessFocusMap = { [x in SFPrimaryBusinessFocus]: PrimaryBusinessFocus };

const primaryBusinessFocusMapSFToLocal = Object.entries(primaryBusinessFocusMapToSF).reduce(
    (acc: Partial<SFPrimaryBusinessFocusMap>, [key, value]) => {
        return { ...acc, [value]: key };
    },
    {}
) as SFPrimaryBusinessFocusMap;

const businessTypes = [
    'Auction House',
    'Consignment',
    'Gallery',
    'Showroom',
    'Design Agency',
    'Maker',
    'Artist',
    'Primary Market Gallery',
    'Secondary Market Gallery',
    'Retailer',
    'Secondary Market Platform',
    'Wholesaler',
    'Manufacturer',
    'Reseller',
    'Individual',
] as const;

export type BusinessType = (typeof businessTypes)[number];

export const businessTypeMap: {
    [fieldName in PrimaryBusinessFocus]: BusinessType[];
} = {
    [FURNITURE_VINTAGE]: ['Auction House', 'Gallery', 'Showroom', 'Individual'],
    [FURNITURE_CONTEMPORARY]: [
        'Design Agency',
        'Manufacturer',
        'Gallery',
        'Maker',
        'Showroom',
        'Individual',
    ],
    [FINE_ART]: [
        'Artist',
        'Auction House',
        'Primary Market Gallery',
        'Secondary Market Gallery',
        'Individual',
    ],
    [JEWELRY_ESTATE]: ['Auction House', 'Retailer', 'Secondary Market Platform', 'Individual'],
    [JEWELRY_CONTEMPORARY]: [
        'Auction House',
        'Maker',
        'Manufacturer',
        'Retailer',
        'Wholesaler',
        'Secondary Market Platform',
        'Individual',
    ],
    [FASHION_VINTAGE]: ['Consignment', 'Reseller', 'Individual'],
    [FASHION_CONTEMPORARY]: ['Maker', 'Retailer', 'Consignment', 'Reseller'],
};

export const estimatedInventorySizeValues = [
    '1-15',
    '16-50',
    '51-100',
    '101-250',
    '251-500',
    '501-1000',
    '1001+',
] as const;

export const soldLastYearValues = ['1-10', '11-50', '51-100', '101-1000', '1001+'] as const;

export const estimatedMonthlyUploadsValues = ['1-15', '16-50', '51-100', '100+'] as const;

export const hoursManagePerWeekValues = ['1-5', '6-10', '11-20', '21+'] as const;

export type Currency = 'USD' | 'GBP' | 'EUR';

export type EstimatedInventorySize = (typeof estimatedInventorySizeValues)[number];

export type SoldLastYear = (typeof soldLastYearValues)[number];

export type EstimatedMonthlyUploads = (typeof estimatedMonthlyUploadsValues)[number];

export type HoursManagePerWeek = (typeof hoursManagePerWeekValues)[number];

export type FormDataContactDetails = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    businessName: string;
    email: string;
    line1: string;
    country: string;
    zipCode: string;
    city: string;
    state: string;
    eori: string;
    experienceSellingOnline?: boolean;
    hasWebsiteOrSocialMedia?: boolean;
    socialNetworks?: SocialNetworkList;
};

export type FormDataBusinessDetails = {
    primaryBusinessFocus?: PrimaryBusinessFocus;
    businessType?: BusinessType;
    galleryRepresentation?: boolean;
    whereRepresented: string;
    specialty: string;
    estimatedInventorySize?: EstimatedInventorySize;
    averageItemPriceCurrency?: Currency;
    averageItemPriceValue: string;
    numSoldLastYear?: SoldLastYear;
    estimatedMonthlyUploads?: EstimatedMonthlyUploads;
    isSellerOnOtherMarketPlace?: boolean;
    otherMarketPlaceDetails: string;
    hoursManagePerWeek?: HoursManagePerWeek;
};

export const FURNITURE = 'Furniture' as const;
export const JEWELRY = 'Jewelry' as const;
export const ART = 'Art' as const;
export const FASHION = 'Fashion' as const;

const verticals = [FURNITURE, JEWELRY, ART, FASHION] as const;

const verticalMap = {
    [FURNITURE_VINTAGE]: FURNITURE,
    [FURNITURE_CONTEMPORARY]: FURNITURE,
    [FINE_ART]: ART,
    [JEWELRY_ESTATE]: JEWELRY,
    [JEWELRY_CONTEMPORARY]: JEWELRY,
    [FASHION_VINTAGE]: FASHION,
    [FASHION_CONTEMPORARY]: FASHION,
} as const;

export type VerticalType = (typeof verticals)[number];

export const MATERIALS = 'materials' as const;
export const RESTORATION_AND_MODIFICATIONS = 'restorationAndModifications' as const;
export const MEDIUM = 'medium' as const;
export const MORE_SIZES_AND_EDITIONS = 'moreSizesAndEditions' as const;
export const METALS = 'metals' as const;
export const STONES = 'stones' as const;
export const STONE_CUT = 'stoneCut' as const;
export const CUSTOMIZABLE = 'customizable' as const;

export type BusinessItem = {
    title: string;
    category: string;
    subCategory: string;
    description: string;
    creatorArtistDesigner?: SingleOnChangeArgs;
    [MATERIALS]?: string;
    [MEDIUM]?: string;
    [METALS]?: string[];
    [STONES]?: string[];
    [STONE_CUT]?: string;
    [CUSTOMIZABLE]?: boolean;
    price: string;
    period: string;
    condition: string;
    [MORE_SIZES_AND_EDITIONS]?: boolean;
    [RESTORATION_AND_MODIFICATIONS]?: boolean;
    imageFiles: File[];
};

const VerticalSpecificField = [
    MATERIALS,
    RESTORATION_AND_MODIFICATIONS,
    MEDIUM,
    MORE_SIZES_AND_EDITIONS,
    METALS,
    STONES,
    STONE_CUT,
    CUSTOMIZABLE,
] as const;

export type VerticalSpecificField = (typeof VerticalSpecificField)[number];

export const SEARCH_CREATOR = 'searchCreator';
export const SEARCH_ARTIST = 'searchArtist';
export const SEARCH_DESIGNER = 'searchDesigner';

type SearchCopyKey = typeof SEARCH_CREATOR | typeof SEARCH_ARTIST | typeof SEARCH_DESIGNER;

export const verticalSpecificsMap: {
    [value in VerticalType]: {
        creatorArtistDesignerKey: SearchCopyKey;
        fields: VerticalSpecificField[];
    };
} = {
    [FURNITURE]: {
        creatorArtistDesignerKey: SEARCH_CREATOR,
        fields: [MATERIALS, RESTORATION_AND_MODIFICATIONS],
    },
    [ART]: {
        creatorArtistDesignerKey: SEARCH_ARTIST,
        fields: [MEDIUM, MORE_SIZES_AND_EDITIONS],
    },
    [FASHION]: {
        creatorArtistDesignerKey: SEARCH_DESIGNER,
        fields: [],
    },
    [JEWELRY]: {
        creatorArtistDesignerKey: SEARCH_CREATOR,
        fields: [METALS, STONES, STONE_CUT, CUSTOMIZABLE],
    },
};

export type FormDataBusinessInventory = {
    items: Array<Partial<BusinessItem>>;
};

export type FormDataConsignorInventoryDetails = {
    estimatedInventorySize: string;
    averageItemPriceCurrency?: Currency;
};

export const SINGLE_LOCATION = 'Single location';
export const MULTIPLE_LOCATIONS = 'Multiple locations';

export type FormDataAdditionalDetails = {
    preferredLanguage: string;
    associations: string;
    fairs: string;
    locations?: typeof SINGLE_LOCATION | typeof MULTIPLE_LOCATIONS;
    locationsWithinSelectedCountry?: boolean;
    ownInventory?: boolean;
    consignmentBasis?: boolean;
};

export type ConsignorItem = {
    creator: SingleOnChangeArgs;
    vertical: VerticalType | null;
    subcategory: SingleOnChangeArgs;
    howItemWasAcquired: string;
    condition: string;
    price: string;
    isAdded: boolean;
    imageFiles: File[];
};

export type BusinessDetailsKeys = keyof FormDataBusinessDetails;

export type ContactDetailsKeys = keyof FormDataContactDetails;

export type AdditionalDetailsKeys = keyof FormDataAdditionalDetails;

export type ReducerState = {
    step?: ApplicationSteps;
    leadId: string;
    vertical: VerticalType;
    formDataContactDetails: FormDataContactDetails;
    formDataBusinessDetails: FormDataBusinessDetails;
    formDataBusinessInventory: FormDataBusinessInventory;
    formDataConsignorInventory: FormDataConsignorInventoryDetails;
    formDataAdditionalDetails: FormDataAdditionalDetails;
    currentItemIndex: number;
    consignorItems: ConsignorItem[];
};

export type ReducerActions =
    | {
          type: 'SET_STEP';
          step: ApplicationSteps;
      }
    | {
          type: 'SET_CREATED_LEAD_ID';
          leadId: string;
          businessFocus?: PrimaryBusinessFocus;
      }
    | { type: 'SET_FORM_CONTACT_DETAILS'; formDataContactDetails: Partial<FormDataContactDetails> }
    | {
          type: 'SET_FORM_BUSINESS_DETAILS';
          formDataBusinessDetails: Partial<FormDataBusinessDetails>;
      }
    | {
          type: 'SET_BUSINESS_ITEM';
          index: number;
          item: Partial<BusinessItem>;
      }
    | {
          type: 'SET_BUSINESS_ITEM_FIELD';
          index: number;
          fieldValue: Partial<BusinessItem>;
      }
    | {
          type: 'REMOVE_BUSINESS_ITEM';
          index: number;
      }
    | {
          type: 'SET_FORM_ADDITIONAL_DETAILS';
          formDataAdditionalDetails: Partial<FormDataAdditionalDetails>;
      }
    | {
          type: 'SET_FORM_CONSIGNOR_INVENTORY_DETAILS';
          formDataConsignorInventory: Partial<FormDataConsignorInventoryDetails>;
      }
    | { type: 'SET_CONSIGNOR_ITEM_DATA'; itemIndex: number; item: Partial<ConsignorItem> }
    | {
          type: 'SET_CONSIGNOR_ACTIVE_ITEM_INDEX';
          itemIndex: number;
      }
    | {
          type: 'ADD_CONSIGNOR_ITEM';
      }
    | {
          type: 'REMOVE_CONSIGNOR_ITEM';
      };

export const blankState: ReducerState = {
    step: undefined,
    leadId: '',
    vertical: ART,
    formDataContactDetails: {
        businessName: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        line1: '',
        country: '',
        zipCode: '',
        city: '',
        state: '',
        eori: '',
        experienceSellingOnline: undefined,
        hasWebsiteOrSocialMedia: undefined,
        socialNetworks: undefined,
    },
    formDataBusinessDetails: {
        primaryBusinessFocus: undefined,
        businessType: undefined,
        galleryRepresentation: undefined,
        whereRepresented: '',
        specialty: '',
        estimatedInventorySize: undefined,
        averageItemPriceCurrency: undefined,
        averageItemPriceValue: '',
        numSoldLastYear: undefined,
        estimatedMonthlyUploads: undefined,
        isSellerOnOtherMarketPlace: undefined,
        otherMarketPlaceDetails: '',
        hoursManagePerWeek: undefined,
    },
    formDataBusinessInventory: {
        items: [],
    },
    formDataConsignorInventory: {
        estimatedInventorySize: '',
        averageItemPriceCurrency: undefined,
    },
    formDataAdditionalDetails: {
        preferredLanguage: '',
        associations: '',
        fairs: '',
        locations: undefined,
        locationsWithinSelectedCountry: undefined,
        ownInventory: undefined,
        consignmentBasis: undefined,
    },
    currentItemIndex: 0,
    consignorItems: [],
};

export const createInitialState = (viewer: SalesforceLeadResponse): ReducerState => {
    if (!viewer?.readFromSF) {
        return blankState;
    }
    const {
        recordId,
        company,
        firstName,
        lastName,
        phone,
        email,
        country,
        state: addressState,
        city,
        street,
        postalCode,
        primaryBusinessFocus: primaryBusinessFocusSF,
        eori,
        hasExperienceSellingOnline,
        businessType,
        galleryRepresentation,
        whereRepresented,
        specialty,
        estimatedInventorySize,
        currencyISOCode,
        averageItemPrice,
        numSoldLastYear,
        estimatedMonthlyUploads,
        isSellerOnOtherMarketPlace,
        otherMarketPlaceDetails,
        hoursManagePerWeek,
        instagram,
        facebook,
        website,
        status,
    } = viewer.salesforceSellerLead || {};
    const primaryBusinessFocus: PrimaryBusinessFocus | undefined = primaryBusinessFocusSF
        ? primaryBusinessFocusMapSFToLocal[primaryBusinessFocusSF as SFPrimaryBusinessFocus]
        : undefined;
    let currentStep: ApplicationSteps = !primaryBusinessFocusSF
        ? 'BUSINESS_STEP2'
        : 'BUSINESS_STEP3';
    if (estimatedMonthlyUploads) {
        currentStep = 'BUSINESS_STEP4';
    }
    if (status !== 'Needs More Info' && status !== 'Open') {
        currentStep = 'BUSINESS_CONFIRMATION';
    }

    const socialNetworks: SocialNetworkList = [];
    if (instagram) {
        socialNetworks.push({ network: 'Instagram', socialUrl: instagram });
    }
    if (facebook) {
        socialNetworks.push({ network: 'Facebook', socialUrl: facebook });
    }
    if (website) {
        socialNetworks.push({ network: 'Website', socialUrl: website });
    }
    const initialState: ReducerState = recordId
        ? {
              ...blankState,
              step: currentStep,
              leadId: recordId || '',
              vertical: primaryBusinessFocus ? verticalMap[primaryBusinessFocus] : ART,
              formDataContactDetails: {
                  firstName: firstName || '',
                  lastName: lastName || '',
                  phoneNumber: phone || '',
                  businessName: company || '',
                  email: email || '',
                  line1: street || '',
                  country: country || '',
                  zipCode: postalCode || '',
                  city: city || '',
                  state: addressState || '',
                  eori: eori || '',
                  experienceSellingOnline: !!hasExperienceSellingOnline,
                  hasWebsiteOrSocialMedia: !!instagram || !!facebook || !!website || undefined,
                  socialNetworks,
              },
              formDataBusinessDetails: {
                  primaryBusinessFocus: primaryBusinessFocus || undefined,
                  businessType: (businessType as BusinessType) || undefined,
                  galleryRepresentation: galleryRepresentation || undefined,
                  whereRepresented: whereRepresented || '',
                  specialty: specialty || '',
                  estimatedInventorySize:
                      (estimatedInventorySize as EstimatedInventorySize) || undefined,
                  averageItemPriceCurrency: (currencyISOCode as Currency) || undefined,
                  averageItemPriceValue: averageItemPrice || '',
                  numSoldLastYear: (numSoldLastYear as SoldLastYear) || undefined,
                  estimatedMonthlyUploads:
                      (estimatedMonthlyUploads as EstimatedMonthlyUploads) || undefined,
                  isSellerOnOtherMarketPlace: isSellerOnOtherMarketPlace ?? undefined,
                  otherMarketPlaceDetails: otherMarketPlaceDetails || '',
                  hoursManagePerWeek: (hoursManagePerWeek as HoursManagePerWeek) || undefined,
              },
          }
        : blankState;
    return initialState;
};

export function reducer(state: ReducerState, action: ReducerActions): ReducerState {
    switch (action.type) {
        case 'SET_STEP':
            return { ...state, step: action.step };
        case 'SET_CREATED_LEAD_ID':
            return {
                ...state,
                leadId: action.leadId,
            };
        case 'SET_FORM_CONTACT_DETAILS': {
            return {
                ...state,
                formDataContactDetails: {
                    ...state.formDataContactDetails,
                    ...action.formDataContactDetails,
                },
            };
        }
        case 'SET_FORM_BUSINESS_DETAILS': {
            const vertical = action.formDataBusinessDetails?.primaryBusinessFocus
                ? verticalMap[action.formDataBusinessDetails.primaryBusinessFocus]
                : null;
            return {
                ...state,
                ...(vertical && { vertical }),
                formDataBusinessDetails: {
                    ...state.formDataBusinessDetails,
                    ...action.formDataBusinessDetails,
                },
            };
        }
        case 'SET_BUSINESS_ITEM': {
            const { index, item } = action;
            const items = state.formDataBusinessInventory.items;
            items[index] = item;
            return {
                ...state,
                formDataBusinessInventory: {
                    items,
                },
            };
        }
        case 'SET_BUSINESS_ITEM_FIELD': {
            const { index, fieldValue } = action;
            const items = state.formDataBusinessInventory.items;
            const item = { ...items[index], ...fieldValue };
            items[index] = item;
            return {
                ...state,
                formDataBusinessInventory: {
                    items,
                },
            };
        }
        case 'REMOVE_BUSINESS_ITEM': {
            const { index } = action;
            const items = state.formDataBusinessInventory.items.filter(
                (_, itemIndex) => itemIndex !== index
            );
            return {
                ...state,
                formDataBusinessInventory: {
                    items,
                },
            };
        }
        case 'SET_FORM_ADDITIONAL_DETAILS': {
            return {
                ...state,
                formDataAdditionalDetails: {
                    ...state.formDataAdditionalDetails,
                    ...action.formDataAdditionalDetails,
                },
            };
        }
        case 'SET_CONSIGNOR_ITEM_DATA': {
            return {
                ...state,
                consignorItems: Object.assign(state.consignorItems, {
                    [action.itemIndex]: {
                        ...state.consignorItems?.[action.itemIndex],
                        ...action.item,
                    },
                }),
            };
        }
        case 'SET_CONSIGNOR_ACTIVE_ITEM_INDEX': {
            return {
                ...state,
                currentItemIndex: action.itemIndex,
            };
        }
        case 'ADD_CONSIGNOR_ITEM': {
            return {
                ...state,
                consignorItems: [
                    ...state.consignorItems,
                    {
                        creator: null,
                        vertical: null,
                        subcategory: null,
                        howItemWasAcquired: '',
                        condition: '',
                        price: '',
                        isAdded: false,
                        imageFiles: [],
                    },
                ],
            };
        }
        case 'REMOVE_CONSIGNOR_ITEM': {
            return {
                ...state,
                consignorItems: state.consignorItems.slice(0, -1),
            };
        }
        case 'SET_FORM_CONSIGNOR_INVENTORY_DETAILS': {
            return {
                ...state,
                formDataConsignorInventory: {
                    ...state.formDataConsignorInventory,
                    ...action.formDataConsignorInventory,
                },
            };
        }
        default:
            return state;
    }
}
